import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Zap, LogIn, AlertCircle, User, Lock } from "lucide-react";
import { useAuth } from "../lib/auth-context";

export default function Login() {
  const { user, login, error } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await login(username, password);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-[#F5F6F8]" data-testid="login-page">
      {/* Left side: dark brand panel */}
      <div className="hidden lg:flex lg:w-1/2 text-white flex-col justify-between p-12 relative overflow-hidden"
           style={{ background: "linear-gradient(135deg,#0F2A1A 0%,#14532D 60%,#16A34A 100%)" }}>
        {/* Decorative leaf pattern */}
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, white 1px, transparent 1px), radial-gradient(circle at 70% 70%, white 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center shadow-lg">
              <Zap size={24} strokeWidth={2.5} className="text-green-800" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-2xl leading-tight tracking-tight">
                VOLT<span className="text-green-300">.IA</span>
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-green-200">
                Movilidad eléctrica sostenible
              </div>
            </div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading font-bold text-4xl tracking-tight leading-tight">
            Acelera la transición a flotas cero emisiones
          </h1>
          <p className="text-green-100 mt-4 max-w-md leading-relaxed">
            Plataforma técnica de análisis de viabilidad eléctrica para líneas de autobús de cualquier
            operador, concesión o región.
          </p>
          <div className="mt-8 flex gap-3 flex-wrap">
            {["Energía", "Orografía", "Climatología", "CAPEX/OPEX", "GTFS"].map((l) => (
              <span
                key={l}
                className="px-3 py-1.5 rounded-full text-xs font-semibold border border-green-300/40 text-green-100 bg-white/5"
              >
                {l}
              </span>
            ))}
          </div>
        </div>
        <div className="text-xs text-green-200/70 relative">
          VOLT.IA · {new Date().getFullYear()}
        </div>
      </div>

      {/* Right side: form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#14532D 0%,#16A34A 100%)" }}>
              <Zap size={18} className="text-white" />
            </div>
            <div className="font-heading font-extrabold text-lg">
              VOLT<span className="text-green-700">.IA</span>
            </div>
          </div>

          <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">
            Acceso técnico
          </div>
          <h2 className="font-heading font-bold text-2xl mt-1 mb-1">Iniciar sesión</h2>
          <p className="text-sm text-gray-500 mb-7">
            Introduce tus credenciales para acceder al panel del estudio.
          </p>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="kpi-label">Usuario</label>
              <div className="relative mt-1">
                <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  required
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="login-username"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-gray-900"
                />
              </div>
            </div>
            <div>
              <label className="kpi-label">Contraseña</label>
              <div className="relative mt-1">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-gray-900"
                />
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-900 rounded-md p-2.5 text-xs" data-testid="login-error">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="btn-primary w-full justify-center mt-2"
            >
              <LogIn size={14} />
              {loading ? "Accediendo…" : "Acceder"}
            </button>
          </form>

          <p className="mt-8 text-xs text-gray-400 leading-relaxed">
            ¿Has olvidado tus credenciales? Contacta con el administrador del estudio.
          </p>
        </div>
      </div>
    </div>
  );
}
